// Modal Component
// Professional modal dialog using design system tokens

import React, { useEffect } from 'react';
import { useTheme } from '../../design-system/ThemeProvider';
import { NavigationIcon } from '../Navigation/NavigationIcon';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true
}) => {
  const { theme } = useTheme();

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return { maxWidth: '400px' };
      case 'lg':
        return { maxWidth: '800px' };
      case 'xl':
        return { maxWidth: '1200px' };
      default:
        return { maxWidth: '600px' };
    }
  };

  const overlayStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.componentTokens.modal.overlay.background,
    backdropFilter: theme.componentTokens.modal.overlay.backdropFilter,
    zIndex: theme.componentTokens.modal.overlay.zIndex,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing[4]
  };

  const contentStyles: React.CSSProperties = {
    backgroundColor: theme.componentTokens.modal.content.background,
    borderRadius: theme.componentTokens.modal.content.borderRadius,
    boxShadow: theme.componentTokens.modal.content.shadow,
    width: '100%',
    ...getSizeStyles(),
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative'
  };

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.componentTokens.modal.content.padding,
    borderBottom: `1px solid ${theme.componentTokens.modal.header.borderBottom}`,
    marginBottom: 0
  };

  const bodyStyles: React.CSSProperties = {
    padding: theme.componentTokens.modal.content.padding
  };

  return (
    <div style={overlayStyles} onClick={onClose}>
      <div style={contentStyles} onClick={(e) => e.stopPropagation()}>
        {(title || showCloseButton) && (
          <div style={headerStyles}>
            {title && (
              <h2 style={{
                margin: 0,
                ...theme.typographyStyles.h3,
                color: theme.semanticColors.text.primary
              }}>
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: theme.spacing[2],
                  borderRadius: theme.borderRadius.md,
                  color: theme.semanticColors.text.secondary,
                  transition: theme.transitions.duration.fast
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.semanticColors.surface.hover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <NavigationIcon name="X" size={20} />
              </button>
            )}
          </div>
        )}
        <div style={bodyStyles}>
          {children}
        </div>
      </div>
    </div>
  );
};